<template>
  <v-dialog v-model="dialog" max-width="800px">
    <v-overlay :value="loading" absolute opacity="0.7">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </v-overlay>
    <v-card>
      <v-card-title>编辑 - {{ formData.lessonName }}</v-card-title>
      <v-card-text>
        <div class="video-player-container mb-6">
          <video
            v-if="formData.videoFileUrl"
            controls
            class="video-player"
            :src="formData.videoFileUrl"
          >
            您的浏览器不支持 HTML5 视频播放
          </video>
          <div v-else class="video-placeholder d-flex align-center justify-center">
            <span class="grey--text">暂无视频</span>
          </div>
        </div>

        <v-form ref="form" v-model="valid">
          <div>
            <v-file-input
              v-model="formData.videoFile"
              label="更新视频"
              accept="video/*"
              show-size
              hint="如不更新视频可不上传"
              persistent-hint
            ></v-file-input>
          </div>

          <!-- 课程名称 -->
          <div class="px-4">
            <div class="d-flex align-center">
              <template v-if="!isEditingName">
                <span class="grey--text text--darken-1">课程名称 :  {{ formData.lessonName }} </span>
                <v-btn icon small class="ml-2" 
                @click="startEditingName">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <div v-else>
                <v-text-field
                v-model="editingName"
                label="课程名称"
                :rules="[
                  v => !!v || '请输入课程名称',
                  v => v.length <= 20 || '课程名称不能超过20个字符'
                ]"
                required
                class="flex-grow-1 mr-2"
              ></v-text-field>
              <v-btn small color="primary"
                  @click="saveEditingName">保存</v-btn>
                  <v-btn small text class="ml-2"
                  @click="cancelEditingName">取消</v-btn>
              </div>
            </div>

            <!-- 课程描述 -->
            <div>
              <div class="d-flex align-start">
                <template v-if="!isEditingDescription">
                  <span class="grey--text text--darken-1">
                    课程描述 : {{ formData.description || '暂无描述' }}
                  </span>
                  <v-btn icon small class="ml-2" @click="startEditingDescription">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <div v-else class="d-flex align-start">
                  <v-text-field
                    v-model="editingDescription"
                    label="课程描述"
                    :rules="[
                      v => v.length <=200 || '课程描述不能超过200个字'
                    ]"
                    counter="200"
                    hide-details="auto"
                    @keyup.esc="cancelEditingDescription"
                  ></v-text-field>
                  <v-btn small color="primary"
                  @click="saveDescription">保存</v-btn>
                  <v-btn small text class="ml-2"
                  @click="cancelEditingDescription">取消</v-btn>
                </div>
              </div>
            </div>

            <div class="px-4 mt-4">
              <div class="d-flex align-center">
                <template v-if="!isEditingSortOrder">
                  <span class="grey--text text--darken-1">排序值: {{ formData.sortOrder }}</span>
                  <v-btn icon small class="ml-2" @click="startEditingSortOrder">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <div v-else>
                  <v-text-field
                    v-model.number="editingSortOrder"
                    type="number"
                    label="排序值"
                    :rules="[v => v >= 0 || '排序值必须≥0']"
                    required
                    class="flex-grow-1 mr-2"
                  ></v-text-field>
                  <v-btn small color="primary" @click="saveSortOrder">保存</v-btn>
                  <v-btn small text class="ml-2" @click="cancelEditingSortOrder">取消</v-btn>
                </div>
              </div>
            </div>

          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="close">取消</v-btn>
        <v-btn color="blue darken-1" text @click="submit">确定</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { courseApi } from '@/api/course'
import { uploadMultipartFile } from '@/utils/ossUploader'

export default {
  name: 'LessonDetailPopup',
  props: {
    item: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      dialog: false,
      valid: false,
      isEditingName: false,
      isEditingDescription: false,
      isEditingSortOrder: false,
      editingSortOrder: 0,
      editingName: '',
      editingDescription: '',
      formData: {
        id: '',
        courseTopicId: '',
        lessonName: '',
        videoFile: null,
        videoFileUrl: '',
        description: '',
        sortOrder: 0,
      },
      courseTopics: [] // 需要从后端获取课程主题列表
    }
  },
  methods: {
    open(courseTopicId, item) {
      this.resetState();
      this.dialog = true;

      this.formData = {
        id: item.lessonId,
        courseTopicId: courseTopicId,
        lessonName: item.lessonName,
        videoFile: null,
        videoFileUrl: item.videoFileUrl,
        description: item.description,
        sortOrder: item.sortOrder
      };
      // 初始化編輯變量
      this.editingName = item.lessonName;
      this.editingDescription = item.description || '';
      this.editingSortOrder = item.sortOrder || 0;
    },
    
    close() {
      this.dialog = false;
      this.resetState();
    },
    
    resetState() {
      this.isEditingName = false;
      this.isEditingDescription = false;
      this.editingName = '';
      this.editingDescription = '';
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    // 名稱編輯相關方法
    startEditingName() {
      this.editingName = this.formData.lessonName;
      this.isEditingName = true;
    },
    
    cancelEditingName() {
      this.editingName = this.formData.lessonName;
      this.isEditingName = false;
    },
    
    saveEditingName() {
      if (this.editingName && this.editingName.length <= 20) {
        this.formData.lessonName = this.editingName;
        this.isEditingName = false;
      }
    },

    // 描述編輯相關方法
    startEditingDescription() {
      this.editingDescription = this.formData.description || '';
      this.isEditingDescription = true;
    },
    
    cancelEditingDescription() {
      this.editingDescription = this.formData.description || '';
      this.isEditingDescription = false;
    },
    
    saveDescription() {
      if (this.editingDescription.length <= 200) {
        this.formData.description = this.editingDescription;
        this.isEditingDescription = false;
      }
    },

    // 排序值編輯方法
    startEditingSortOrder() {
      this.editingSortOrder = this.formData.sortOrder;
      this.isEditingSortOrder = true;
    },
    
    cancelEditingSortOrder() {
      this.isEditingSortOrder = false;
    },
    
    saveSortOrder() {
      if (this.editingSortOrder >= 0) {
        this.formData.sortOrder = this.editingSortOrder;
        this.isEditingSortOrder = false;
      }
    },

    // async submit() {
    //   if (!this.$refs.form.validate()) return;

    //   try {
    //     const formData = new FormData();
    //     if (this.formData.videoFile) {
    //       formData.append('videoFile', this.formData.videoFile);
    //     }

    //     const params = {
    //       id: this.formData.id,
    //       courseTopicId: this.formData.courseTopicId,
    //       lessonName: this.formData.lessonName,
    //       description: this.formData.description,
    //       videoFile: formData
    //     };

    //     console.log('提交的参数：', params);

    //     await courseApi.updateLesson(params);

    //     this.$emit('lesson-updated');
    //     this.close();
    //     this.$toast.success('课程更新成功');
    //   } catch (error) {
    //     const errorMessage = error.response?.data?.message || error.message || '未知错误';
    //     this.$toast.error('更新失败：' + errorMessage);
    //   }
    // }
    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      if(this.formData.videoFile) {
        const fileName = this.formData.videoFile.name;
        const extension = fileName.substring(fileName.lastIndexOf('.') + 1);
        const params = {
          type: 'VideoCourse',
          fileExtension: extension,
          courseTopicId: this.formData.courseTopicId,
          lessonId: this.formData.id,
          lessonName: this.formData.lessonName,
          description: this.formData.description,
          sortOrder: this.formData.sortOrder
        };
        const createdStsResp = await courseApi.getUploadMultipartUrl(params);
        const sts = createdStsResp.data;
        // 2. 上传到OSS, 分片上传
        await uploadMultipartFile(this.formData.videoFile, sts, null)
        try{
          this.$emit('lesson-updated');
          this.close();
          this.$toast.success('课程更新成功');
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message || '未知错误';
          this.$toast.error('更新失败：' + errorMessage);
        } finally {
          this.loading = false;
        }

      } else {
        try {
          const params = {
            id: this.formData.id,
            courseTopicId: this.formData.courseTopicId,
            lessonName: this.formData.lessonName,
            description: this.formData.description,
            sortOrder: this.formData.sortOrder
          };

          await courseApi.updateLesson(params);

          this.$emit('lesson-updated');
          this.close();
          this.$toast.success('课程更新成功');
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message || '未知错误';
          this.$toast.error('更新失败：' + errorMessage);
        } finally {
          this.loading = false;
        }
      }
      
    }
  }
}
</script>

<style scoped>
.video-player-container {
  width: 100%;
  height: 400px;
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.video-placeholder {
  width: 100%;
  height: 100%;
  background-color: #f5f5f5;
  border-radius: 4px;
}
</style> 